// Audit-log view-model. The backend doesn't have a dedicated global
// /api/v1/audit endpoint yet, so this aggregates per-user audit entries
// from fetchStaffMember(:id). That's N+1 requests by design; cheap enough
// in dev (~30 users) but the backend should publish a paginated endpoint
// before this ships to production.
// TODO(user-mgmt): replace aggregation with /api/v1/audit/recent.

import type { AuditEventDTO } from '../../../api/audit';
import { fetchStaff, fetchStaffMember, type StaffAuditEntry } from '../../../api/staff';

export type AuditAction =
  | 'created'
  | 'invited'
  | 'linked-fh'
  | 'role-changed'
  | 'region-added'
  | 'region-removed'
  | 'deactivated'
  | 'reactivated'
  | 'updated';

export interface AuditRow {
  id: string;
  ts: string;
  changedAt: Date;
  actor: string;
  target: string;
  action: AuditAction;
  /** Optional structured diff for richer rendering. */
  before?: unknown;
  after?: unknown;
  /** Human label of the field that changed (e.g. "regionIds"). */
  field?: string;
}

const ACTION_LABEL: Record<AuditAction, string> = {
  created: 'Created',
  invited: 'Invited',
  'linked-fh': 'Linked FH staff',
  'role-changed': 'Role changed',
  'region-added': 'Region added',
  'region-removed': 'Region removed',
  deactivated: 'Deactivated',
  reactivated: 'Reactivated',
  updated: 'Updated',
};

const ACTION_COLOR: Record<AuditAction, string> = {
  created: '#0e874b',
  invited: '#257bea',
  'linked-fh': '#BA7517',
  'role-changed': '#7A5AE0',
  'region-added': '#0c6b3d',
  'region-removed': '#cc6726',
  deactivated: '#993C1D',
  reactivated: '#18a85e',
  updated: '#5d675d',
};

export function actionLabel(a: AuditAction): string {
  return ACTION_LABEL[a];
}

export function actionColor(a: AuditAction): string {
  return ACTION_COLOR[a];
}

type Classified = { action: AuditAction; field?: string; before?: unknown; after?: unknown };

/** Per-field classifier. Returns null to mean "this field doesn't map to a
 *  recognized action — keep looking". */
const FIELD_CLASSIFIERS: Record<
  string,
  (change: { before: unknown; after: unknown }, field: string) => Classified
> = {
  isActive: (c, field) => ({
    action: c.after === false ? 'deactivated' : 'reactivated',
    field,
    before: c.before,
    after: c.after,
  }),
  regionIds: classifyRegionChange,
  regions: classifyRegionChange,
  auth0Sub: (c, field) => ({ action: 'invited', field, before: c.before, after: c.after }),
  // Historical UserEditEvent rows recorded changes to the retired
  // fareharborStaffKey column; they still need classifying (BON-528).
  fareharborStaffKey: (c, field) => ({
    action: 'linked-fh',
    field,
    before: c.before,
    after: c.after,
  }),
  fareharborUsername: (c, field) => ({
    action: 'linked-fh',
    field,
    before: c.before,
    after: c.after,
  }),
  role: (c, field) => ({ action: 'role-changed', field, before: c.before, after: c.after }),
};

function classifyRegionChange(
  change: { before: unknown; after: unknown },
  field: string,
): Classified {
  const before = Array.isArray(change.before) ? change.before : [];
  const after = Array.isArray(change.after) ? change.after : [];
  const added = after.length > before.length;
  return { action: added ? 'region-added' : 'region-removed', field, before, after };
}

/** Derive an action label from the raw field-changes blob. We classify the
 *  *first* recognized field; multiple-field edits collapse to "updated". */
function classifyAction(
  fieldChanges: Record<string, { before: unknown; after: unknown }>,
): Classified {
  for (const [field, change] of Object.entries(fieldChanges)) {
    const handler = FIELD_CLASSIFIERS[field];
    if (handler) return handler(change, field);
  }
  return { action: 'updated' };
}

export function toAuditRow(target: string, entry: StaffAuditEntry): AuditRow {
  const raw = entry.fieldChanges;
  const fieldChanges = (raw && typeof raw === 'object' ? raw : {}) as Record<
    string,
    { before: unknown; after: unknown }
  >;
  const { action, field, before, after } = classifyAction(fieldChanges);
  const changedAt = new Date(entry.changedAt);
  return {
    id: entry.id,
    ts: changedAt.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }),
    changedAt,
    actor: entry.actorEmail ?? entry.actorUserId ?? 'system',
    target,
    action,
    before,
    after,
    field,
  };
}

// ── BON-56: action-stamped audit row mapper ──────────────────────────────
// The global /api/v1/audit/users endpoint returns events whose fieldChanges
// JSON carries a top-level `action` key (stamped by user-management.service
// at write time). Translate those high-signal actions directly here; fall
// back to the legacy field-keyed classifier for low-signal events.

const KNOWN_ACTIONS = new Set([
  'invited',
  'role_changed',
  'regions_changed',
  'deactivated',
  'reactivated',
]);

interface ActionShape {
  action?: unknown;
  roles_before?: unknown;
  roles_after?: unknown;
  rolesAdded?: unknown;
  rolesRemoved?: unknown;
  before?: unknown;
  after?: unknown;
  added?: unknown;
  removed?: unknown;
}

function isActionShape(v: unknown): v is ActionShape {
  return typeof v === 'object' && v !== null;
}

const ACTION_DISPATCH: Record<string, (p: ActionShape) => Classified> = {
  invited: () => ({ action: 'invited' }),
  role_changed: (p) => ({
    action: 'role-changed',
    before: p.roles_before ?? p.rolesRemoved,
    after: p.roles_after ?? p.rolesAdded,
  }),
  regions_changed: (p) => {
    const added = Array.isArray(p.added) ? p.added : [];
    const removed = Array.isArray(p.removed) ? p.removed : [];
    // Single add OR single remove gets the specific label; a mixed
    // add+remove in the same edit collapses to 'updated' — the row's
    // before/after diff renders the full set-change either way.
    let action: AuditAction = 'updated';
    if (added.length > 0 && removed.length === 0) action = 'region-added';
    else if (removed.length > 0 && added.length === 0) action = 'region-removed';
    return { action, before: p.before ?? removed, after: p.after ?? added };
  },
  deactivated: () => ({ action: 'deactivated' }),
  reactivated: () => ({ action: 'reactivated' }),
};

function classifyByAction(payload: ActionShape): Classified {
  const action = typeof payload.action === 'string' ? payload.action : '';
  const handler = ACTION_DISPATCH[action];
  return handler ? handler(payload) : { action: 'updated' };
}

export function toAuditRowFromAction(entry: AuditEventDTO): AuditRow {
  const raw = entry.fieldChanges;
  const payload: ActionShape = isActionShape(raw) ? raw : {};
  const classified =
    typeof payload.action === 'string' && KNOWN_ACTIONS.has(payload.action)
      ? classifyByAction(payload)
      : // Low-signal fallback: treat the payload as a field-keyed diff.
        classifyAction(raw as Record<string, { before: unknown; after: unknown }>);
  const changedAt = new Date(entry.changedAt);
  return {
    id: entry.id,
    ts: changedAt.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }),
    changedAt,
    actor: entry.actor.email ?? entry.actor.userId,
    target: entry.target.name ?? entry.target.email ?? entry.target.userId,
    action: classified.action,
    before: classified.before,
    after: classified.after,
    field: classified.field,
  };
}

/** Fetch the staff list, then fan out detail calls to pull audit per user,
 *  flatten + sort. Slow with many users — should be replaced by a backend
 *  /audit/recent endpoint. */
export async function fetchAggregatedAudit(limit = 200): Promise<AuditRow[]> {
  const staff = await fetchStaff();
  const details = await Promise.all(
    staff.map(async (s) => {
      try {
        const detail = await fetchStaffMember(s.id);
        return { name: detail.name || s.name, audit: detail.audit ?? [] };
      } catch {
        return { name: s.name, audit: [] };
      }
    }),
  );

  const rows: AuditRow[] = [];
  for (const d of details) {
    for (const e of d.audit) rows.push(toAuditRow(d.name, e));
  }
  rows.sort((a, b) => b.changedAt.getTime() - a.changedAt.getTime());
  return rows.slice(0, limit);
}
